import { doc } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorText, LoadingView, SBCard } from "@/components/ui";
import { useAppEnvironmentReport } from "@/features/ai/AppEnvironmentScreen";
import type {
  SBotAppEnvironmentControl,
  SBotAppEnvironmentSection,
  SBotAppEnvironmentSurfaceReport,
} from "@/features/ai/services/appEnvironmentContext";
import { db } from "@/firebase/app";
import { useActiveLeagueScope } from "@/features/competitions/useLeagueCompetitions";
import { useDoc } from "@/hooks/firestoreHooks";
import { parseLeague } from "@/models/catalog";
import { parseMemberSummary, type LeagueMemberSummary } from "@/models/memberSummary";
import { parseAthleteValue, parseFieldSchema } from "@/models/schema";
import {
  initials,
  parseUser,
  preferredName,
  resolvedLeagueID,
  USER_ROLE_LABELS,
  type SBotMembership,
  type SBotSocialProfile,
  type SBotUser,
} from "@/models/user";
import {
  athleteMatchesWorkspace,
  hasActiveStaffProfileMembership,
  publicAthleteHeadshotURL,
  staffSummaryMatchesWorkspace,
  visibleAthleteProfileFields,
} from "./profileAccess";
import "./home.css";
import "./profileScreens.css";

export function AthleteProfileView() {
  const { athleteID } = useParams<{ athleteID: string }>();
  const { currentUser, isSBotLabsAdmin } = useAuth();
  const { leagueID, organizationID } = useActiveLeagueScope();
  const athlete = useDoc(
    athleteID ? doc(db, "leagueAthleteValues", athleteID) : null,
    parseAthleteValue,
  );
  const schema = useDoc(
    leagueID ? doc(db, "leagueAthleteSchemas", leagueID) : null,
    parseFieldSchema,
  );
  const league = useDoc(leagueID ? doc(db, "leagues", leagueID) : null, parseLeague);

  const fields = useMemo(
    () => athlete.data
      ? visibleAthleteProfileFields(athlete.data, schema.data, currentUser, isSBotLabsAdmin)
      : [],
    [athlete.data, schema.data, currentUser, isSBotLabsAdmin],
  );
  const environmentReport = useMemo<SBotAppEnvironmentSurfaceReport>(() => {
    const title = athlete.data?.name ?? "Athlete Profile";
    return {
      identity: "athlete-profile",
      owningTab: "home",
      breadcrumbs: ["Home", "Rosters", title],
      destinationType: "athleteProfile",
      entityDisplayName: athlete.data?.name,
      surfaceLevel: "leaf",
      surfaceDepth: 2,
      surfacePresentation: "route",
      screen: {
        title,
        contextCompleteness: "basic",
        state: athlete.error || schema.error
          ? "error"
          : athlete.loading || schema.loading
            ? "loading"
            : athlete.data
              ? "visible"
              : "unavailable",
        sections: [{
          id: "athlete-profile-current-view",
          title: "Current View",
          controls: [{ role: "status", label: title }],
        }],
      },
    };
  }, [athlete.data, athlete.error, athlete.loading, schema.error, schema.loading]);
  useAppEnvironmentReport(environmentReport);

  if (!athleteID || !leagueID) {
    return <ProfileState title="Athlete unavailable" message="Select a league workspace before opening an athlete profile." />;
  }
  if (athlete.loading || schema.loading) return <LoadingView label="Loading athlete profile…" />;
  if (athlete.error || schema.error) {
    return <ProfileState title="Athlete unavailable" message={athlete.error ?? schema.error ?? "The athlete profile could not be loaded."} />;
  }
  if (!athlete.data) {
    return <ProfileState title="Athlete not found" message="This athlete no longer exists in the active workspace." />;
  }
  if (athlete.data.leagueID !== leagueID || (organizationID && athlete.data.organizationID !== organizationID)) {
    return <ProfileState title="Athlete restricted" message="This athlete is not available in the active workspace." />;
  }
  if (athlete.data.status !== "active") {
    return <ProfileState title="Athlete unavailable" message="This athlete is no longer active in the current league." />;
  }
  if (!athleteMatchesWorkspace(athlete.data, leagueID, organizationID)) {
    return <ProfileState title="Athlete restricted" message="This athlete is not available in the active workspace." />;
  }

  const headshotURL = publicAthleteHeadshotURL(athlete.data, schema.data);
  const leagueName = league.data?.name ?? currentUser?.activeContext?.displayName ?? "League";

  return (
    <ProfilePage>
      <ProfileHero
        name={athlete.data.name}
        photoURL={headshotURL}
        fallbackInitials={nameInitials(athlete.data.name)}
        tags={["League managed", ...(athlete.data.athleteUserID ? ["Account linked"] : [])]}
      />
      <SBCard className="profile-section">
        <h2>{leagueName}</h2>
        <p className="home-hint">This athlete profile is managed by the league.</p>
      </SBCard>
      <SBCard className="profile-section">
        <h2>Athlete details</h2>
        {fields.length === 0 ? (
          <p className="home-hint">No profile details are available for your current league access.</p>
        ) : (
          <dl className="profile-details">
            {fields.map((field) => <Detail key={field.id} label={field.label} value={field.value} />)}
          </dl>
        )}
      </SBCard>
    </ProfilePage>
  );
}

export function StaffProfileView() {
  const { summaryID } = useParams<{ summaryID: string }>();
  const { currentUser } = useAuth();
  const { leagueID, organizationID } = useActiveLeagueScope();
  const [disclosures, setDisclosures] = useState<StaffProfileDisclosures>(DEFAULT_STAFF_PROFILE_DISCLOSURES);
  useEffect(() => {
    setDisclosures(DEFAULT_STAFF_PROFILE_DISCLOSURES);
  }, [summaryID]);
  const summary = useDoc(
    summaryID ? doc(db, "leagueMemberSummaries", summaryID) : null,
    parseMemberSummary,
  );
  const canLoadProfile = !!summary.data
    && !!leagueID
    && staffSummaryMatchesWorkspace(summary.data, leagueID, organizationID);
  const profile = useDoc(
    canLoadProfile && summary.data ? doc(db, "users", summary.data.userID) : null,
    parseUser,
  );
  const league = useDoc(leagueID ? doc(db, "leagues", leagueID) : null, parseLeague);
  const leagueName = league.data?.name ?? currentUser?.activeContext?.displayName ?? "League";
  const reportState: StaffProfileReportState = !summaryID || !leagueID
    ? "unavailable"
    : summary.loading
      ? "loading"
      : summary.error
        ? "error"
        : !summary.data
          ? "missing"
          : !staffSummaryMatchesWorkspace(summary.data, leagueID, organizationID)
            ? "restricted"
            : profile.loading
              ? "loading"
              : profile.error
                ? "error"
                : !profile.data
                  ? "missing"
                  : !hasActiveStaffProfileMembership(profile.data, leagueID)
                    ? "restricted"
                    : "visible";
  const environmentReport = useMemo<SBotAppEnvironmentSurfaceReport>(() => {
    const title = reportState === "visible" && profile.data
      ? preferredName(profile.data) || summary.data?.displayName || "Staff Profile"
      : "Staff Profile";
    return buildStaffProfileEnvironmentReport({
      identity: summaryID ? `staff-profile:${summaryID}` : "staff-profile",
      title,
      state: reportState,
      user: reportState === "visible" ? profile.data ?? undefined : undefined,
      member: reportState === "visible" ? summary.data ?? undefined : undefined,
      leagueName,
      disclosures,
    });
  }, [disclosures, leagueName, profile.data, reportState, summary.data, summaryID]);
  useAppEnvironmentReport(environmentReport);

  if (!summaryID || !leagueID) {
    return <ProfileState title="Staff profile unavailable" message="Select a league workspace before opening a staff profile." />;
  }
  if (summary.loading) return <LoadingView label="Loading staff profile…" />;
  if (summary.error) return <ProfileState title="Profile unavailable" message={summary.error} />;
  if (!summary.data) {
    return <ProfileState title="Staff member not found" message="This staff member no longer exists in the active workspace." />;
  }
  if (!staffSummaryMatchesWorkspace(summary.data, leagueID, organizationID)) {
    return <ProfileState title="Profile restricted" message="This staff member is not visible in the active workspace." />;
  }
  if (profile.loading) return <LoadingView label="Loading staff profile…" />;
  if (profile.error) {
    return <ProfileState title="Profile unavailable" message={profile.error} />;
  }
  if (!profile.data) {
    return <ProfileState title="Profile unavailable" message="This staff member's user profile could not be found." />;
  }
  if (!hasActiveStaffProfileMembership(profile.data, leagueID)) {
    return <ProfileState title="Profile restricted" message="This user no longer has active staff access in the current league." />;
  }

  const member = summary.data;
  const user = profile.data;
  const memberships = user.memberships;
  const updateDisclosure = (key: keyof StaffProfileDisclosures, open: boolean) => {
    setDisclosures((current) => current[key] === open ? current : { ...current, [key]: open });
  };

  return (
    <ProfilePage>
      <ProfileHero
        name={preferredName(user) || member.displayName}
        photoURL={user.photoURL ?? member.photoURL}
        fallbackInitials={initials(user)}
        tags={member.roles.map((role) => USER_ROLE_LABELS[role])}
      />
      <ReadOnlySection title="Profile" open={disclosures.profile} onOpenChange={(open) => updateDisclosure("profile", open)}>
        <dl className="profile-details">
          <Detail label="First name" value={user.firstName} />
          <Detail label="Last name" value={user.lastName} />
          <Detail label="Display name" value={user.displayName} />
          <Detail label="Roles" value={user.roles.map((role) => USER_ROLE_LABELS[role]).join(", ")} />
          <Detail label="Onboarding" value={onboardingLabel(user.onboardingStatus)} />
        </dl>
      </ReadOnlySection>
      {user.bio?.trim() && <ReadOnlySection title="Bio" open={disclosures.bio} onOpenChange={(open) => updateDisclosure("bio", open)}><p>{user.bio.trim()}</p></ReadOnlySection>}
      <ReadOnlySection title="Contact" open={disclosures.contact} onOpenChange={(open) => updateDisclosure("contact", open)}>
        <dl className="profile-details">
          <Detail label="Email" value={user.email} />
          <Detail label="Secondary email" value={user.secondaryEmail} />
          <Detail label="Phone" value={user.phoneNumber} />
          {user.address && <>
            <Detail label="Address line 1" value={user.address.line1} />
            <Detail label="Address line 2" value={user.address.line2} />
            <Detail label="City" value={user.address.city} />
            <Detail label="State / region" value={user.address.region} />
            <Detail label="Postal code" value={user.address.postalCode} />
            <Detail label="Country" value={user.address.country} />
          </>}
        </dl>
      </ReadOnlySection>
      <ReadOnlySection title="Affiliations" open={disclosures.affiliations} onOpenChange={(open) => updateDisclosure("affiliations", open)}>
        <dl className="profile-details">
          <Detail label="League" value={leagueName} />
          <Detail label="Organizations" value={member.organizationNames.join(", ")} />
          {user.activeContext && <>
            <Detail label="Active workspace" value={user.activeContext.displayName} />
            <Detail label="Active role" value={USER_ROLE_LABELS[user.activeContext.role]} />
            <Detail label="Active target" value={user.activeContext.targetID} />
            <Detail label="Active sport" value={user.activeContext.sportID} />
            <Detail label="Active league" value={user.activeContext.leagueID} />
            <Detail label="Active organization" value={user.activeContext.organizationID} />
          </>}
        </dl>
        {memberships.length > 0 && (
          <details
            className="profile-subsection"
            open={disclosures.memberships}
            onToggle={(event) => updateDisclosure("memberships", event.currentTarget.open)}
          >
            <summary>Memberships</summary>
            {memberships.map((membership) => <MembershipDetails key={membershipKey(membership)} membership={membership} />)}
          </details>
        )}
      </ReadOnlySection>
      {user.socialProfiles.length > 0 && <ReadOnlySection title="Social Profiles" open={disclosures.social} onOpenChange={(open) => updateDisclosure("social", open)}>
        <dl className="profile-details">
          {user.socialProfiles.map((social, index) => (
            <Detail key={`${social.platform}:${index}`} label={socialLabel(social)} value={social.url ?? social.handle} />
          ))}
        </dl>
      </ReadOnlySection>}
      <ReadOnlySection title="Sports" open={disclosures.sports} onOpenChange={(open) => updateDisclosure("sports", open)}>
        <dl className="profile-details">
          <Detail label="Favorite sports" value={user.favoriteSports.join(", ")} />
          <Detail label="Favorite teams" value={user.favoriteTeams.join(", ")} />
          {user.sportsPreference && <>
            <Detail label="Primary sport" value={nestedString(user.sportsPreference, "primarySport", "name")} />
            <Detail label="League" value={nestedString(user.sportsPreference, "league", "name")} />
            <Detail label="Team" value={nestedString(user.sportsPreference, "team", "displayName")} />
            <Detail label="Tracked athletes" value={preferenceAthleteNames(user.sportsPreference)} />
            <Detail label="Preference updated" value={formatUnknownDate(user.sportsPreference.updatedAt)} />
          </>}
        </dl>
      </ReadOnlySection>
      <ReadOnlySection title="Account Data" open={disclosures.account} onOpenChange={(open) => updateDisclosure("account", open)}>
        <dl className="profile-details">
          <Detail label="User ID" value={user.id} />
          <Detail label="Photo URL" value={user.photoURL} />
          <Detail label="Photo storage path" value={user.photoStoragePath} />
          <Detail label="Created" value={formatDate(user.createdAt)} />
          <Detail label="Updated" value={formatDate(user.updatedAt)} />
          <Detail label="Last login" value={formatDate(user.lastLoginAt)} />
        </dl>
      </ReadOnlySection>
    </ProfilePage>
  );
}

type StaffProfileReportState = "loading" | "error" | "missing" | "restricted" | "unavailable" | "visible";

interface StaffProfileDisclosures {
  profile: boolean;
  bio: boolean;
  contact: boolean;
  affiliations: boolean;
  memberships: boolean;
  social: boolean;
  sports: boolean;
  account: boolean;
}

const DEFAULT_STAFF_PROFILE_DISCLOSURES: StaffProfileDisclosures = {
  profile: true,
  bio: true,
  contact: true,
  affiliations: true,
  memberships: false,
  social: true,
  sports: true,
  account: false,
};

function buildStaffProfileEnvironmentReport({
  identity,
  title,
  state,
  user,
  member,
  leagueName,
  disclosures,
}: {
  identity: string;
  title: string;
  state: StaffProfileReportState;
  user?: SBotUser;
  member?: LeagueMemberSummary;
  leagueName: string;
  disclosures: StaffProfileDisclosures;
}): SBotAppEnvironmentSurfaceReport {
  return {
    identity,
    owningTab: "home",
    breadcrumbs: ["Home", "Staff", title],
    destinationType: "staffProfile",
    entityDisplayName: title === "Staff Profile" ? undefined : title,
    surfaceLevel: "leaf",
    surfaceDepth: 2,
    surfacePresentation: "route",
    screen: {
      title,
      contextCompleteness: "semantic",
      state,
      sections: state === "visible" && user && member
        ? staffProfileEnvironmentSections(user, member, leagueName, disclosures)
        : [staffProfileStatusSection(state === "visible" ? "unavailable" : state)],
    },
  };
}

function staffProfileStatusSection(state: Exclude<StaffProfileReportState, "visible">): SBotAppEnvironmentSection {
  const statuses: Record<Exclude<StaffProfileReportState, "visible">, readonly [string, string]> = {
    loading: ["Loading staff profile", "The authorized profile is still loading."],
    error: ["Profile unavailable", "The staff profile could not be loaded."],
    missing: ["Staff member not found", "The staff profile is no longer available."],
    restricted: ["Profile restricted", "This staff profile is not visible in the active workspace."],
    unavailable: ["Staff profile unavailable", "Select a league workspace before opening a staff profile."],
  };
  const status = statuses[state];
  return {
    id: "staff-profile-status",
    title: "Status",
    systemImage: state === "loading" ? "hourglass" : state === "restricted" ? "lock.fill" : "exclamationmark.triangle.fill",
    controls: [{ role: "status", label: status[0], value: status[1] }],
  };
}

function staffProfileEnvironmentSections(
  user: SBotUser,
  member: LeagueMemberSummary,
  leagueName: string,
  disclosures: StaffProfileDisclosures,
): SBotAppEnvironmentSection[] {
  const sections: SBotAppEnvironmentSection[] = [];
  const bio = user.bio?.trim();
  if (bio) {
    sections.push(disclosureSection({
      id: "staff-profile-bio",
      title: "Bio",
      systemImage: "text.quote",
      expanded: disclosures.bio,
      availableContentLabels: ["Bio"],
      controls: [environmentValue("Bio", bio, "text.quote")],
    }));
  }

  sections.push(disclosureSection({
    id: "staff-profile-profile",
    title: "Profile",
    systemImage: "person.text.rectangle",
    expanded: disclosures.profile,
    availableContentLabels: ["First name", "Last name", "Display name", "Roles", "Onboarding"],
    controls: [
      environmentValue("First name", user.firstName, "person.fill"),
      environmentValue("Last name", user.lastName, "person.fill"),
      environmentValue("Display name", user.displayName, "person.crop.circle"),
      environmentValue("Roles", user.roles.map((role) => USER_ROLE_LABELS[role]).join(", "), "person.badge.key.fill"),
      environmentValue("Onboarding", onboardingLabel(user.onboardingStatus), "checkmark.seal.fill"),
    ],
  }));

  const contactControls: SBotAppEnvironmentControl[] = [
    environmentValue("Email", user.email, "envelope.fill"),
    environmentValue("Secondary email", user.secondaryEmail, "envelope.badge.fill"),
    environmentValue("Phone", user.phoneNumber, "phone.fill"),
  ];
  const contactLabels = ["Email", "Secondary email", "Phone"];
  if (user.address) {
    const addressValues: Array<[string, string | undefined, string]> = [
      ["Address line 1", user.address.line1, "house.fill"],
      ["Address line 2", user.address.line2, "house.fill"],
      ["City", user.address.city, "building.2.fill"],
      ["State / region", user.address.region, "map.fill"],
      ["Postal code", user.address.postalCode, "number"],
      ["Country", user.address.country, "globe.americas.fill"],
    ];
    contactLabels.push(...addressValues.map(([label]) => label));
    contactControls.push(...addressValues.map(([label, value, image]) => environmentValue(label, value, image)));
  }
  sections.push(disclosureSection({
    id: "staff-profile-contact",
    title: "Contact",
    systemImage: "envelope.fill",
    expanded: disclosures.contact,
    availableContentLabels: contactLabels,
    controls: contactControls,
  }));

  const affiliationLabels = ["League", "Organizations"];
  const affiliationControls: SBotAppEnvironmentControl[] = [
    environmentValue("League", leagueName, "trophy.fill"),
    environmentValue("Organizations", member.organizationNames.join(", "), "building.2.fill"),
  ];
  if (user.activeContext) {
    affiliationLabels.push("Active workspace", "Active role");
    affiliationControls.push(
      environmentValue("Active workspace", user.activeContext.displayName, "scope"),
      environmentValue("Active role", USER_ROLE_LABELS[user.activeContext.role], "person.badge.key.fill"),
    );
  }
  if (user.memberships.length > 0) {
    affiliationLabels.push("Memberships");
    affiliationControls.push({
      id: "staff-profile-memberships-disclosure",
      role: "button",
      label: "Memberships",
      value: disclosures.memberships ? "Expanded" : "Collapsed",
      systemImage: "person.2.badge.key.fill",
      isEnabled: true,
      isSelected: disclosures.memberships,
      selectionValue: disclosures.memberships ? "expanded" : "collapsed",
    });
    if (disclosures.memberships) {
      affiliationControls.push(...user.memberships.map(membershipSummaryControl));
    }
  }
  sections.push(disclosureSection({
    id: "staff-profile-affiliations",
    title: "Affiliations",
    systemImage: "person.3.fill",
    expanded: disclosures.affiliations,
    availableContentLabels: affiliationLabels,
    controls: affiliationControls,
  }));

  if (user.socialProfiles.length > 0) {
    sections.push(disclosureSection({
      id: "staff-profile-social",
      title: "Social Profiles",
      systemImage: "link",
      expanded: disclosures.social,
      availableContentLabels: user.socialProfiles.map(socialLabel),
      controls: user.socialProfiles.map((social) => environmentValue(socialLabel(social), social.url ?? social.handle, "link.circle.fill")),
    }));
  }

  const sportsControls: SBotAppEnvironmentControl[] = [
    environmentValue("Favorite sports", user.favoriteSports.join(", "), "star.fill"),
    environmentValue("Favorite teams", user.favoriteTeams.join(", "), "flag.checkered"),
  ];
  const sportsLabels = ["Favorite sports", "Favorite teams"];
  if (user.sportsPreference) {
    const values: Array<[string, string | undefined, string]> = [
      ["Primary sport", nestedString(user.sportsPreference, "primarySport", "name"), "sportscourt.fill"],
      ["League", nestedString(user.sportsPreference, "league", "name"), "trophy.fill"],
      ["Team", nestedString(user.sportsPreference, "team", "displayName"), "shield.fill"],
      ["Tracked athletes", preferenceAthleteNames(user.sportsPreference), "person.2.fill"],
      ["Preference updated", formatUnknownDate(user.sportsPreference.updatedAt), "clock.fill"],
    ];
    sportsLabels.push(...values.map(([label]) => label));
    sportsControls.push(...values.map(([label, value, image]) => environmentValue(label, value, image)));
  }
  sections.push(disclosureSection({
    id: "staff-profile-sports",
    title: "Sports",
    systemImage: "figure.run.circle.fill",
    expanded: disclosures.sports,
    availableContentLabels: sportsLabels,
    controls: sportsControls,
  }));

  sections.push(disclosureSection({
    id: "staff-profile-account",
    title: "Account Data",
    systemImage: "doc.text.magnifyingglass",
    expanded: disclosures.account,
    availableContentLabels: ["Created", "Updated", "Last login", "Restricted identifiers omitted"],
    controls: [
      environmentValue("Created", formatDate(user.createdAt), "calendar.badge.plus"),
      environmentValue("Updated", formatDate(user.updatedAt), "calendar.badge.clock"),
      environmentValue("Last login", formatDate(user.lastLoginAt), "rectangle.portrait.and.arrow.right"),
      { role: "status", label: "Restricted identifiers omitted", value: "User IDs and photo storage locations are not shared." },
    ],
  }));
  return sections;
}

function disclosureSection({
  id,
  title,
  systemImage,
  expanded,
  availableContentLabels,
  controls,
}: {
  id: string;
  title: string;
  systemImage: string;
  expanded: boolean;
  availableContentLabels: string[];
  controls: SBotAppEnvironmentControl[];
}): SBotAppEnvironmentSection {
  return {
    id,
    title,
    systemImage,
    disclosureState: expanded ? "expanded" : "collapsed",
    availableContentLabels,
    controls: expanded ? controls : [],
    visibleItemCount: expanded ? controls.length : 0,
    hiddenItemCount: expanded ? 0 : availableContentLabels.length,
  };
}

function environmentValue(label: string, value: string | undefined, systemImage: string): SBotAppEnvironmentControl {
  return { role: "value", label, value: value?.trim() || "Not provided", systemImage };
}

function membershipSummaryControl(membership: SBotMembership): SBotAppEnvironmentControl {
  const status = membership.status === "active" ? "Active" : membership.status;
  return {
    role: "row",
    label: membership.displayName,
    value: `Role: ${USER_ROLE_LABELS[membership.role]}; Status: ${status}; Target type: ${membership.targetType}`,
    systemImage: "person.2.badge.key.fill",
  };
}

function ProfilePage({ children }: { children: React.ReactNode }) {
  return <div className="list-screen profile-page">{children}</div>;
}

function ProfileHero({
  name,
  photoURL,
  fallbackInitials,
  tags,
}: {
  name: string;
  photoURL?: string;
  fallbackInitials: string;
  tags: string[];
}) {
  return (
    <header className="profile-hero">
      <div className="profile-avatar">
        {photoURL ? <img src={photoURL} alt="" /> : fallbackInitials}
      </div>
      <div>
        <h1>{name}</h1>
        <div className="profile-tags">
          {tags.map((tag) => <span className="badge" key={tag}>{tag}</span>)}
        </div>
      </div>
    </header>
  );
}

function ReadOnlySection({
  title,
  open,
  onOpenChange,
  children,
}: {
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <SBCard className="profile-section">
      <details open={open} onToggle={(event) => onOpenChange(event.currentTarget.open)}>
        <summary><h2>{title}</h2></summary>
        <div className="profile-section-content">{children}</div>
      </details>
    </SBCard>
  );
}

function Detail({ label, value }: { label: string; value?: string | null }) {
  return <div className="profile-detail"><dt>{label}</dt><dd>{value?.trim() || "Not provided"}</dd></div>;
}

function MembershipDetails({ membership }: { membership: SBotMembership }) {
  return (
    <div className="profile-membership">
      <strong>{membership.displayName}</strong>
      <dl className="profile-details">
        <Detail label="Role" value={USER_ROLE_LABELS[membership.role]} />
        <Detail label="Status" value={membership.status === "active" ? "Active" : membership.status} />
        <Detail label="Target" value={`${membership.targetType}: ${membership.targetID}`} />
        <Detail label="Sport ID" value={membership.sportID} />
        <Detail label="League ID" value={resolvedLeagueID(membership)} />
        <Detail label="Organization ID" value={membership.organizationID} />
        <Detail label="Invite code" value={membership.inviteCode} />
        <Detail label="Created" value={formatDate(membership.createdAt)} />
        <Detail label="Updated" value={formatDate(membership.updatedAt)} />
      </dl>
    </div>
  );
}

function ProfileState({ title, message }: { title: string; message: string }) {
  return (
    <div className="list-screen profile-page">
      <SBCard className="profile-section">
        <h1>{title}</h1>
        <ErrorText>{message}</ErrorText>
      </SBCard>
    </div>
  );
}

function membershipKey(membership: SBotMembership): string {
  return `${membership.targetType}:${membership.targetID}:${membership.role}`;
}

function nameInitials(name: string): string {
  const value = name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
  return value || "SB";
}

function onboardingLabel(value: SBotUser["onboardingStatus"]): string {
  if (value === "complete") return "Complete";
  if (value === "pendingInvite") return "Pending invite";
  return "Not started";
}

function socialLabel(profile: SBotSocialProfile): string {
  return profile.platform === "x" ? "X" : profile.platform.charAt(0).toUpperCase() + profile.platform.slice(1);
}

function formatDate(value?: Date): string | undefined {
  return value
    ? new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(value)
    : undefined;
}

function nestedString(source: Record<string, unknown>, key: string, nestedKey: string): string | undefined {
  const nested = source[key];
  if (!nested || typeof nested !== "object") return undefined;
  const value = (nested as Record<string, unknown>)[nestedKey];
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function preferenceAthleteNames(preference: Record<string, unknown>): string | undefined {
  if (!Array.isArray(preference.athletes)) return undefined;
  const names = preference.athletes.flatMap((athlete) => {
    if (!athlete || typeof athlete !== "object") return [];
    const displayName = (athlete as Record<string, unknown>).displayName;
    return typeof displayName === "string" && displayName.trim() ? [displayName.trim()] : [];
  });
  return names.length > 0 ? names.join(", ") : undefined;
}

function formatUnknownDate(value: unknown): string | undefined {
  let date: Date | undefined;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) date = parsed;
  } else if (value && typeof value === "object") {
    const timestamp = value as { toDate?: () => unknown; seconds?: unknown };
    if (typeof timestamp.toDate === "function") {
      try {
        const converted = timestamp.toDate();
        if (converted instanceof Date) date = converted;
      } catch {
        return undefined;
      }
    } else if (typeof timestamp.seconds === "number") {
      date = new Date(timestamp.seconds * 1_000);
    }
  }
  return date && !Number.isNaN(date.getTime()) ? formatDate(date) : undefined;
}
